// screens/ClientBuckets.tsx — B18d: what every client is telling us, by
// bucket.
//
// ⚑ NOT `Clients.tsx`. That name is TAKEN — by Rexx's client registry, with
// its own `.clients` CSS and its own `clients` tab key. This screen is the
// personal pack's, its tab key is `buckets`, and only its LABEL says
// "Clients". (Writing it as Clients.tsx overwrote Rexx's screen for about a
// minute during the build; git had it back, and the lesson is that a
// vertical's names are not free to reuse just because the word fits.)
//
// The morning question this answers is not "what did Kettle & Co say" —
// People answers that — but "what are my clients reporting". So the unit is
// the BUCKET, and the clients appear inside it.
//
// ⚑ NOTHING AUTO-RESOLVES (the spec's own words). This screen reads. An
// item taps through to the actual conversation, where the doors that DO act
// already live; a bucket that quietly emptied itself would be worse than a
// full one.
//
// The summary is B13b's async contract, third consumer: a thin bucket says
// so rather than writing a paragraph about two lines, a failed model says
// so rather than going quiet, and nothing is stored — what you read was
// composed for this look at it.
import { useEffect, useState } from 'preact/hooks';
import { InterpretationLabel, SourceCitationChip } from '@vowric/ui';
import {
  ApiError,
  getBucketSummary,
  listBuckets,
  proposeClientWork,
  listAccounts,
  startAccount,
  listParties,
  type BucketApi,
  type BucketSummaryAnswerApi,
  type AccountListRowApi,
  type AccountPhase,
  type PartyApi,
} from '../lib/api';
import './ClientBuckets.css';

export interface ClientBucketsProps {
  tenantId: string;
  onOpenThread: (id: string) => void;
  // K1 (console kernel, c2-account-kernel.md) — the Accounts section atop
  // this screen drills into the Account spread.
  onOpenAccount: (id: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready' };

const POLL_MS = 2_500;
const POLL_MAX = 24;

function when(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60_000))}m`;
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

const ACCOUNT_PHASES: { id: AccountPhase; label: string }[] = [
  { id: 'design_partner', label: 'Design partner' },
  { id: 'billable', label: 'Billable' },
];

/** K1 — the Accounts section atop the Clients (buckets) screen. Its own
 * load/error/empty states, independent of the bucket list below: an
 * account read failing must not blank out the client-work buckets, and the
 * reverse. */
function AccountsSection({ tenantId, onOpenAccount }: { tenantId: string; onOpenAccount: (id: string) => void }) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [accounts, setAccounts] = useState<AccountListRowApi[]>([]);
  const [starting, setStarting] = useState(false);
  const [parties, setParties] = useState<PartyApi[]>([]);
  const [partyId, setPartyId] = useState('');
  const [startedOn, setStartedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [phase, setPhase] = useState<AccountPhase>('design_partner');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  function refresh() {
    return listAccounts(tenantId)
      .then((rows) => { setAccounts(rows); setLoad({ phase: 'ready' }); })
      .catch((err: unknown) => {
        setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the accounts could not be read' });
      });
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function openStart() {
    setStarting(true);
    setNote(null);
    try {
      const rows = await listParties(tenantId);
      setParties(rows);
      setPartyId(rows[0]?.id ?? '');
    } catch {
      // The form still opens — a picker with nothing to show is honest;
      // failing the whole affordance over it is not.
    }
  }

  async function submitStart() {
    if (!partyId) return;
    setBusy(true);
    setNote(null);
    try {
      const account = await startAccount(tenantId, { partyId, startedOn, phase });
      const party = parties.find((p) => p.id === partyId);
      setNote(`account started · ${party?.displayName ?? 'the party'} · ${phase === 'billable' ? 'billable' : 'design partner'} · from ${account.startedOn}`);
      setStarting(false);
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'the account was not started');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section class="cbuckets__accounts">
      <div class="cbuckets__eyebrow">Accounts</div>
      {load.phase === 'loading' ? (
        <div class="cbuckets__skeleton" aria-hidden="true"><div class="cbuckets__skeleton-row" /></div>
      ) : load.phase === 'error' ? (
        <div class="cbuckets__say">{(load as { message: string }).message}</div>
      ) : accounts.length === 0 ? (
        <div class="cbuckets__say">
          No accounts yet.
          <br />
          <span class="cbuckets__say-soft">The first one starts from a client's name.</span>
        </div>
      ) : (
        <div class="cbuckets__list">
          {accounts.map((a) => (
            <button type="button" class="cbuckets__row" key={a.id} onClick={() => onOpenAccount(a.id)} style={{ width: '100%' }}>
              <span class="cbuckets__item-main">
                <span class="cbuckets__item-who">{a.party?.displayName ?? '(no party)'}</span>
                <span class="cbuckets__item-snippet">{a.phase.replace(/_/g, ' ')} · since {a.daysInPhase ?? '?'}d</span>
              </span>
            </button>
          ))}
        </div>
      )}
      {!starting ? (
        <button type="button" class="cbuckets__accounts-act" onClick={() => void openStart()}>Start an account</button>
      ) : (
        <div class="cbuckets__staged" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <select value={partyId} disabled={busy} onChange={(e) => setPartyId((e.target as HTMLSelectElement).value)}>
            {parties.length === 0 ? <option value="">No parties yet</option> : null}
            {parties.map((p) => <option value={p.id} key={p.id}>{p.displayName}</option>)}
          </select>
          <input type="date" value={startedOn} disabled={busy} onInput={(e) => setStartedOn((e.target as HTMLInputElement).value)} />
          <select value={phase} disabled={busy} onChange={(e) => setPhase((e.target as HTMLSelectElement).value as AccountPhase)}>
            {ACCOUNT_PHASES.map((p) => <option value={p.id} key={p.id}>{p.label}</option>)}
          </select>
          <button type="button" class="cbuckets__accounts-act" disabled={busy || !partyId} onClick={() => void submitStart()}>Start</button>
          <button type="button" class="cbuckets__accounts-act" disabled={busy} onClick={() => setStarting(false)}>Never mind</button>
        </div>
      )}
      {note ? <div class="cbuckets__staged-line">{note}</div> : null}
    </section>
  );
}

export function ClientBuckets({ tenantId, onOpenThread, onOpenAccount }: ClientBucketsProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [buckets, setBuckets] = useState<BucketApi[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [summaries, setSummaries] = useState<Record<string, BucketSummaryAnswerApi>>({});
  // B18e: which item is being read into a card, and what came of each tap.
  const [staging, setStaging] = useState<string | null>(null);
  const [staged, setStaged] = useState<Record<string, string>>({});

  /* B18e — "this is work". It stages a card in the pen and creates NOTHING:
     the queue that fills itself is the one nobody trusts, so the tap ends at
     Review, and the receipt here says exactly that. A second tap on the same
     item gets the card already waiting rather than a second decision about
     one conversation. */
  async function makeWork(threadId: string) {
    setStaging(threadId);
    try {
      const r = await proposeClientWork(tenantId, threadId);
      setStaged((cur) => ({
        ...cur,
        [threadId]: r.alreadyPending
          ? 'Already waiting in Review.'
          : `In Review for your say-so — “${r.proposed.title}”.`,
      }));
    } catch (err) {
      setStaged((cur) => ({
        ...cur,
        [threadId]: err instanceof ApiError ? err.message : 'that could not be read as work',
      }));
    } finally {
      setStaging(null);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoad({ phase: 'loading' });
    listBuckets(tenantId)
      .then((rows) => {
        if (cancelled) return;
        setBuckets(rows);
        setLoad({ phase: 'ready' });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoad({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'the buckets could not be read',
        });
      });
    return () => { cancelled = true; };
  }, [tenantId]);

  /** Opening a bucket asks for its summary — never on load, because five
   *  model calls to paint a screen nobody has expanded is a cost with no
   *  reader. */
  function toggle(kind: string) {
    const next = open === kind ? null : kind;
    setOpen(next);
    if (!next || summaries[kind]) return;

    let polls = 0;
    const ask = () => {
      getBucketSummary(tenantId, kind)
        .then((answer) => {
          setSummaries((cur) => ({ ...cur, [kind]: answer }));
          if (answer.status === 'composing') {
            polls += 1;
            if (polls < POLL_MAX) setTimeout(ask, POLL_MS);
          }
        })
        .catch(() => {
          setSummaries((cur) => ({ ...cur, [kind]: { status: 'failed', summary: null } }));
        });
    };
    ask();
  }

  function Summary({ kind }: { kind: string }) {
    const answer = summaries[kind];
    if (!answer || answer.status === 'composing') {
      return (
        <div class="cbuckets__summary cbuckets__summary--composing" aria-hidden="true">
          <div class="cbuckets__shimmer" />
          <div class="cbuckets__shimmer cbuckets__shimmer--short" />
        </div>
      );
    }
    if (answer.status === 'not_needed') {
      // The honest floor: reading two items beats reading about them.
      return <div class="cbuckets__quiet">Few enough to just read.</div>;
    }
    if (answer.status === 'failed' || !answer.summary) {
      return <div class="cbuckets__quiet">No summary this time — the items are below.</div>;
    }
    const s = answer.summary;
    return (
      <div class="cbuckets__summary">
        <InterpretationLabel kind="interpreted">{s.text}</InterpretationLabel>
        <div class="cbuckets__cite">
          <SourceCitationChip
            source={`across ${s.parties.length} client${s.parties.length === 1 ? '' : 's'} · ${s.model}`}
            when={`${s.threadIds.length} conversations${s.truncated ? ' · most recent only' : ''}`}
          />
        </div>
      </div>
    );
  }

  const anything = buckets.some((b) => b.threadCount > 0);

  return (
    <div class="cbuckets">
      <AccountsSection tenantId={tenantId} onOpenAccount={onOpenAccount} />

      <div class="cbuckets__eyebrow">Clients</div>

      {load.phase === 'loading' ? (
        <div class="cbuckets__skeleton" aria-hidden="true">
          <div class="cbuckets__skeleton-row" />
          <div class="cbuckets__skeleton-row" />
        </div>
      ) : load.phase === 'error' ? (
        <div class="cbuckets__say">
          The buckets could not be read.
          <br />
          <span class="cbuckets__say-soft">{(load as { message: string }).message}</span>
        </div>
      ) : !anything ? (
        <div class="cbuckets__say">
          Nothing from your clients yet.
          <br />
          <span class="cbuckets__say-soft">
            Bugs, requests, questions and anything else they send land here — from their
            Telegram group, or from their own page.
          </span>
        </div>
      ) : (
        <div class="cbuckets__list">
          {buckets.map((b) => (
            <section class="cbuckets__bucket" key={b.kind}>
              <button
                type="button"
                class="cbuckets__head"
                aria-expanded={open === b.kind}
                disabled={b.threadCount === 0}
                onClick={() => toggle(b.kind)}
              >
                <span class="cbuckets__name">{b.name}</span>
                {/* The count is of EVERYTHING, not of what is shown. */}
                <span class={`cbuckets__count${b.threadCount === 0 ? ' is-empty' : ''}`}>
                  {b.threadCount}
                </span>
              </button>

              {open === b.kind ? (
                <div class="cbuckets__body">
                  <Summary kind={b.kind} />
                  {b.items.map((item) => (
                    /* Two doors on one line, and they are NOT the same door:
                       the row reads the conversation, and the second one
                       proposes work from it. A button inside a button would
                       be neither valid nor operable. */
                    <div class="cbuckets__row" key={item.threadId}>
                      <button
                        type="button"
                        class="cbuckets__item"
                        onClick={() => onOpenThread(item.threadId)}
                      >
                        <span class="cbuckets__item-main">
                          <span class="cbuckets__item-who">{item.partyName}</span>
                          <span class="cbuckets__item-snippet">{item.snippet}</span>
                        </span>
                        <span class="cbuckets__item-meta">
                          <span class="cbuckets__item-count">{item.messageCount}</span>
                          <span class="cbuckets__item-when">{when(item.lastMessageAt)}</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        class="cbuckets__make"
                        disabled={staging === item.threadId}
                        title="Read this as work — it goes to Review for your say-so"
                        onClick={() => void makeWork(item.threadId)}
                      >
                        {staging === item.threadId ? '…' : 'Make work'}
                      </button>
                    </div>
                  ))}
                  {/* Receipts, not acknowledgements: what the tap actually
                      did, in words, where it was tapped. */}
                  {b.items.some((i) => staged[i.threadId]) ? (
                    <div class="cbuckets__staged">
                      {b.items.filter((i) => staged[i.threadId]).map((i) => (
                        <div class="cbuckets__staged-line" key={i.threadId}>{staged[i.threadId]}</div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
